import { useRef } from "react";
import { gtmPush } from "../../functions/gtm";
import TextField from "../../audi-ui-components/TextField";
import RadioFieldset from "../../audi-ui-components/RadioFieldset";
import AddressField from "./AddressField";
import Button from "../../audi-ui-components/Button";
import { formatMobile } from "../../functions/textFormatting";
import { DEFAULT_THEME } from "../../constants";
import {
  requestTypes,
} from "./CVOConstants";

const StepUserDetails = ({ values, errors, touched, setFieldValue, setFieldTouched, theme = DEFAULT_THEME }) => {
  const hasTrackedFName = useRef(false);
  const hasTrackedLName = useRef(false);
  const hasTrackedMobile = useRef(false);
  const hasTrackedEmail = useRef(false);
  const hasTrackedAddress = useRef(false);

  if (values.requestType === requestTypes.PERSONAL_DETAILS_CHANGED) {
    return (
      <div>
        {values.notUsingMyaudi === false && <div className="mb-5">
          <p className="aui-headline-4 mb-4">Sign in to myAudi</p>
          <p className="mb-3">Would you like to sign into myAudi? This will allow you to update your details immediately.</p>
          <div className="d-flex flex-row" style={{gap: "1rem"}}>
            <Button variant="secondary" onClick={() => { setFieldValue('notUsingMyaudi', true); }}>No, continue here</Button>
            <Button variant="primary" href="https://myaudi.com.au/profile">Sign in to myAudi</Button>
          </div>
        </div>}
        <div style={(values.notUsingMyaudi === false) ? {opacity: "0.5", pointerEvents: "none"} : {}}>
          <p className="aui-headline-4 mb-4">Your new details</p>
          <div className="row">
            <div className="col-12 col-small-6 col-medium-12 col-large-6">
              <TextField name="firstName"
                label="First name"
                value={values.firstName}
                onChange={(v) => { setFieldValue('firstName', v); }}
                isInvalid={errors.firstName && touched.firstName}
                invalidMessage={errors.firstName}
                onBlur={() => { setFieldTouched('firstName'); }}
                onFocus={() => { if(!hasTrackedFName.current) {
                  gtmPush("cvoFirstNameClicked", "ChangeVehicleOwnership");
                  hasTrackedFName.current = true;
                }}}
                className="form-field"
                theme={theme === "dark" ? "light" : null}
              />
            </div>
            <div className="col-12 col-small-6 col-medium-12 col-large-6">
              <TextField name="lastName"
                label="Last name"
                value={values.lastName}
                onChange={(v) => { setFieldValue('lastName', v); }}
                isInvalid={errors.lastName && touched.lastName}
                invalidMessage={errors.lastName}
                onBlur={() => { setFieldTouched('lastName'); }}
                onFocus={() => { if(!hasTrackedLName.current) {
                  gtmPush("cvoLastNameClicked", "ChangeVehicleOwnership");
                  hasTrackedLName.current = true;
                }}}
                className="form-field"
                theme={theme === "dark" ? "light" : null}
              />
            </div>
          </div>
          <RadioFieldset name="changedName"
            label="Has your name changed?"
            options={[ {label: "Yes", value: "yes"}, {label: "No", value: "no"} ]}
            value={values.changedName}
            onChange={(v) => {
              setFieldValue('changedName', v);
              gtmPush("cvoHasNameChanged", "ChangeVehicleOwnership", v);
            }}
            onBlur={() => { setFieldTouched('changedName'); }}
            isInvalid={errors.changedName && touched.changedName}
            invalidMessage={errors.changedName}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />

          <TextField name="phone"
            label="Contact number"
            value={values.phone}
            onChange={(v) => { setFieldValue('phone', v); }}
            isInvalid={errors.phone && touched.phone}
            invalidMessage={errors.phone}
            onBlur={() => { setFieldTouched('phone'); }}
            onFocus={() => { if(!hasTrackedMobile.current) {
              gtmPush("cvoMobileNumberClicked", "ChangeVehicleOwnership");
              hasTrackedMobile.current = true;
            }}}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
          <RadioFieldset name="changedPhone"
            label="Has your contact number changed?"
            options={[ {label: "Yes", value: "yes"}, {label: "No", value: "no"} ]}
            value={values.changedPhone}
            onChange={(v) => {
              setFieldValue('changedPhone', v);
              gtmPush("cvoHasContactNumberChanged", "ChangeVehicleOwnership", v);
            }}
            onBlur={() => { setFieldTouched('changedPhone'); }}
            isInvalid={errors.changedPhone && touched.changedPhone}
            invalidMessage={errors.changedPhone}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />

          <TextField name="email"
            label="Email"
            value={values.email}
            onChange={(v) => { setFieldValue('email', v.trim()); }}
            isInvalid={errors.email && touched.email}
            invalidMessage={errors.email}
            onBlur={() => { setFieldTouched('email'); }}
            onFocus={() => { if(!hasTrackedEmail.current) {
              gtmPush("cvoEmailClicked", "ChangeVehicleOwnership");
              hasTrackedEmail.current = true;
            }}}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
          <RadioFieldset name="changedEmail"
            label="Has your email changed?"
            options={[ {label: "Yes", value: "yes"}, {label: "No", value: "no"} ]}
            value={values.changedEmail}
            onChange={(v) => {
              setFieldValue('changedEmail', v);
              gtmPush("cvoHasEmailChanged", "ChangeVehicleOwnership", v);
            }}
            onBlur={() => { setFieldTouched('changedEmail'); }}
            isInvalid={errors.changedEmail && touched.changedEmail}
            invalidMessage={errors.changedEmail}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />

          <AddressField
            values={values} errors={errors} touched={touched}
            setFieldValue={setFieldValue} setFieldTouched={setFieldTouched}
            onFocus={() => { if(!hasTrackedAddress.current) {
              gtmPush("cvoAddressClicked", "ChangeVehicleOwnership");
              hasTrackedAddress.current = true;
            }}}
            theme={theme}
          />
          <RadioFieldset name="changedAddress"
            label="Has your address changed?"
            options={[ {label: "Yes", value: "yes"}, {label: "No", value: "no"} ]}
            value={values.changedAddress}
            onChange={(v) => {
              setFieldValue('changedAddress', v);
              gtmPush("cvoHasAddressChanged", "ChangeVehicleOwnership", v);
            }}
            onBlur={() => { setFieldTouched('changedAddress'); }}
            isInvalid={errors.changedAddress && touched.changedAddress}
            invalidMessage={errors.changedAddress}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
        </div>
      </div>
    );
  }
  return (
    <div>
      <p className="aui-headline-4 mb-4">Your details</p>
      <div className="row">
        <div className="col-12 col-small-6 col-medium-12 col-large-6">
          <TextField name="firstName"
            label="First name"
            value={values.firstName}
            onChange={(v) => { setFieldValue('firstName', v); }}
            isInvalid={errors.firstName && touched.firstName}
            invalidMessage={errors.firstName}
            onBlur={() => { setFieldTouched('firstName'); }}
            onFocus={() => { if(!hasTrackedFName.current) {
              gtmPush("cvoFirstNameClicked", "ChangeVehicleOwnership");
              hasTrackedFName.current = true;
            }}}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
        </div>
        <div className="col-12 col-small-6 col-medium-12 col-large-6">
          <TextField name="lastName"
            label="Last name"
            value={values.lastName}
            onChange={(v) => { setFieldValue('lastName', v); }}
            isInvalid={errors.lastName && touched.lastName}
            invalidMessage={errors.lastName}
            onBlur={() => { setFieldTouched('lastName'); }}
            onFocus={() => { if(!hasTrackedLName.current) {
              gtmPush("cvoLastNameClicked", "ChangeVehicleOwnership");
              hasTrackedLName.current = true;
            }}}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-small-6 col-medium-12 col-large-6">
          <TextField name="phone"
            label="Mobile number"
            value={values.phone}
            onChange={(v) => { v = formatMobile(v); setFieldValue('phone', v); }}
            isInvalid={errors.phone && touched.phone}
            invalidMessage={errors.phone}
            onBlur={() => { setFieldTouched('phone'); }}
            onFocus={() => { if(!hasTrackedMobile.current) {
              gtmPush("cvoMobileNumberClicked", "ChangeVehicleOwnership");
              hasTrackedMobile.current = true;
            }}}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
        </div>
        <div className="col-12 col-small-6 col-medium-12 col-large-6">
          <TextField name="email"
            label="Email"
            value={values.email}
            onChange={(v) => { setFieldValue('email', v.trim()); }}
            isInvalid={errors.email && touched.email}
            invalidMessage={errors.email}
            onBlur={() => { setFieldTouched('email'); }}
            onFocus={() => { if(!hasTrackedEmail.current) {
              gtmPush("cvoEmailClicked", "ChangeVehicleOwnership");
              hasTrackedEmail.current = true;
            }}}
            className="form-field"
            theme={theme === "dark" ? "light" : null}
          />
        </div>
      </div>
      <AddressField
        values={values} errors={errors} touched={touched}
        setFieldValue={setFieldValue} setFieldTouched={setFieldTouched}
        onFocus={() => { if(!hasTrackedAddress.current) {
          gtmPush("cvoAddressClicked", "ChangeVehicleOwnership");
          hasTrackedAddress.current = true;
        }}}
        theme={theme}
      />
    </div>
  );
};

export default StepUserDetails;
